import React from 'react'
import { createPortal } from 'react-dom'
import { useApp } from '../context/AppContext'
import { ApiError, ConflictError, ValidationError, api } from '../api/client'
import { newTimeBlockId } from '../api/ids'
import { conflictMessage } from '../calendar/booking'
import { minutesToTime, timeToMinutes } from '../catalog/format'
import Field from '../screens/settings/Field'
import { saveErrorMessage, showToast } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import './Sheet.css'
import './Booking.css'
import '../screens/settings/Settings.css'

const isGone = (e: unknown) => e instanceof ApiError && e.status === 404

// TimeBlockSheet blocks off part of a day, or edits or removes a block she
// saved. A clash with a booking or another block is warned about first; Block
// anyway saves over it.
export default function TimeBlockSheet() {
  const { state, dispatch } = useApp()
  const prefill = state.timeBlock
  const block = prefill?.block
  // One id for the sheet's lifetime, so a retried or forced save can't block twice.
  const [id] = React.useState(() => newTimeBlockId())

  const [date, setDate] = React.useState(block?.date ?? prefill?.date ?? state.calendarDate)
  const [start, setStart] = React.useState<number | null>(block?.start_minute ?? prefill?.startMinute ?? null)
  const [end, setEnd] = React.useState<number | null>(block?.end_minute ?? prefill?.endMinute ?? null)
  const [label, setLabel] = React.useState(block?.label ?? '')

  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [conflict, setConflict] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')
  const [saving, setSaving] = React.useState(false)

  const close = () => dispatch({ type: 'CLOSE_SHEET' })
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, close, { closable: !saving })
  if (!prefill) return null

  const save = async (force: boolean) => {
    const missing: Record<string, string> = {}
    if (!date) missing.date = 'Choose a date.'
    if (start === null) missing.start_minute = 'Choose a start time.'
    if (end === null) missing.end_minute = 'Choose an end time.'
    else if (start !== null && end <= start) missing.end_minute = 'End after the start.'
    if (Object.keys(missing).length > 0 || start === null || end === null) {
      setErrors(missing)
      return
    }
    setSaving(true)
    setErrors({})
    setError('')
    setConflict(null)
    const input = { label: label.trim() || null, date, start_minute: start, end_minute: end, force }
    try {
      const saved = block ? await api.updateTimeBlock(block.id, input) : await api.createTimeBlock({ id, ...input })
      close()
      dispatch({ type: 'SET_CALENDAR_DATE', payload: saved.date })
      dispatch({ type: 'APPOINTMENTS_CHANGED' })
      showToast(dispatch, block ? 'Block saved' : 'Time blocked')
    } catch (e) {
      if (e instanceof ConflictError) {
        setConflict(conflictMessage(e.conflicts, { date, start_minute: start }, e.blocks))
      } else if (e instanceof ValidationError) {
        setErrors(e.fields)
      } else if (block && isGone(e)) {
        // Removed on another device: the calendar is stale, so reload it.
        setError('This block has been removed.')
        dispatch({ type: 'APPOINTMENTS_CHANGED' })
      } else {
        setError(saveErrorMessage(e))
      }
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!block) return
    setSaving(true)
    setError('')
    try {
      await api.deleteTimeBlock(block.id)
    } catch (e) {
      // Already gone (another device): the aim is met.
      if (!isGone(e)) {
        setError(saveErrorMessage(e))
        setSaving(false)
        return
      }
    }
    close()
    dispatch({ type: 'APPOINTMENTS_CHANGED' })
    showToast(dispatch, 'Block removed')
  }

  const changed = () => setConflict(null)

  const sheet = (
    <div className="sheet-overlay" onClick={(e) => { e.stopPropagation(); if (!saving) close() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Block time" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>{block ? 'Edit block' : 'Block time'}</h2>
        </div>

        <div className="sheet-body booking-form">
          <Field id="block-date" label="Date" error={errors.date}>
            <input id="block-date" type="date" value={date} onChange={(e) => { setDate(e.target.value); changed() }} />
          </Field>
          <Field id="block-start" label="Start" error={errors.start_minute}>
            <input
              id="block-start"
              type="time"
              step={300}
              value={start === null ? '' : minutesToTime(start)}
              onChange={(e) => { setStart(timeToMinutes(e.target.value)); changed() }}
            />
          </Field>
          <Field id="block-end" label="End" error={errors.end_minute}>
            <input
              id="block-end"
              type="time"
              step={300}
              value={end === null ? '' : minutesToTime(end)}
              onChange={(e) => { setEnd(timeToMinutes(e.target.value)); changed() }}
            />
          </Field>
          <Field id="block-label" label="Label (optional)" error={errors.label}>
            <input id="block-label" type="text" maxLength={60} placeholder="Dentist" value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>

          {conflict && (
            <div className="booking-conflict" role="alert">
              <p>{conflict}</p>
            </div>
          )}
          {errors.id && <p className="field-error" role="alert">{errors.id}</p>}
          {error && <p className="field-error" role="alert">{error}</p>}
        </div>

        <div className="sheet-stack">
          <div className="sheet-actions">
            <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => save(conflict !== null)} disabled={saving}>
              {conflict !== null ? 'Block anyway' : block ? 'Save' : 'Block time'}
            </button>
          </div>
          {/* One tap, no confirm: a block is easy to make again. Destructive
              entry points are the red outline (docs/25). */}
          {block && (
            <button type="button" className="btn btn-danger btn-block" onClick={remove} disabled={saving}>
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
